import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Anchor, Box, Button, Divider, Flex, PasswordInput, SimpleGrid, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import InviteAuthShell from '@/PageComponents/Auth/InviteAuthShell'
import WrongAccountScreen from '@/PageComponents/Auth/WrongAccountScreen'
import ScTextControl from '@/components/sc-controls/form-controls/v2/sc-text-control'
import PasswordRequirements from '@/components/sc-controls/password-requirements'
import { PlatformAuthService } from '@/services/platform-auth'
import { clearCookie } from '@/utils/auth'
import Fetch from '@/utils/Fetch'
import Helper from '@/utils/helper'
import * as Enums from '@/utils/enums'
import Storage from '@/utils/storage'

function readInviteFlags() {
  try {
    const raw = sessionStorage.getItem('sc:completeInvite')
    if (!raw) return { requiresPassword: true, requiresProfile: true, email: '', companyName: '', firstName: '', lastName: '' }
    return JSON.parse(raw)
  } catch {
    return { requiresPassword: true, requiresProfile: true, email: '', companyName: '', firstName: '', lastName: '' }
  }
}

function CompleteInvitePage() {
  const flags = readInviteFlags()
  const company = flags.companyName || Storage.getCookie('servCompanyName') || 'ServCraft'
  const email = flags.email || Storage.getCookie('servUserName') || ''
  const [firstName, setFirstName] = useState(flags.firstName || '')
  const [lastName, setLastName] = useState(flags.lastName || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [showPasswords, setShowPasswords] = useState(false)
  const [stage, setStage] = useState<'setup' | 'wrong' | 'done'>('setup')
  const [joinedName, setJoinedName] = useState('')

  const needsPassword = !!flags.requiresPassword
  const needsProfile = flags.requiresProfile !== false
  const joinOnly = !needsPassword && !needsProfile

  const finish = async (first?: string, last?: string, pwd?: string) => {
    setSubmitting(true)
    setErrorMessage('')
    setPasswordError('')
    try {
      const service = new PlatformAuthService()
      if (!joinOnly) {
        await service.completeProfile(first!.trim(), last!.trim(), needsPassword ? pwd : undefined)
        await Fetch.post({
          url: '/Employee/CompleteInvite',
          params: { FirstName: first!.trim(), LastName: last!.trim() }
        })
        Storage.setCookie(Enums.Cookie.servFullName, `${first!.trim()} ${last!.trim()}`.trim(), 7)
      }
      sessionStorage.removeItem('sc:completeInvite')
      setJoinedName((first || flags.firstName || '').trim())
      setStage('done')
    } catch (error: any) {
      setErrorMessage(error?.message || 'Could not finish setting up your account.')
      showNotification({ color: 'red', message: error?.message || 'Could not finish setting up your account.' })
    } finally {
      setSubmitting(false)
    }
  }

  const submit = async () => {
    if (joinOnly) {
      await finish()
      return
    }

    const validation = Helper.validateInputs([
      { key: 'firstName', value: firstName, required: true, type: Enums.ControlType.Text },
      { key: 'lastName', value: lastName, required: true, type: Enums.ControlType.Text },
      ...(needsPassword
        ? [
            { key: 'password', value: password, required: true, type: Enums.ControlType.Text },
            { key: 'confirmPassword', value: confirmPassword, required: true, type: Enums.ControlType.Text }
          ]
        : [])
    ])
    setPasswordError('')
    if (!validation.isValid) {
      setErrorMessage('Please fill in the required fields.')
      return
    }
    if (needsPassword && password !== confirmPassword) {
      setErrorMessage('Passwords do not match.')
      return
    }
    if (needsPassword) {
      const strengthError = Helper.getPasswordStrengthError(password, {
        userwords: [firstName, lastName, email]
      })
      if (strengthError) {
        setPasswordError(strengthError)
        return
      }
    }
    await finish(firstName, lastName, password)
  }

  if (stage === 'done') {
    return (
      <InviteAuthShell>
        <DoneScreen company={company} email={email} firstName={joinedName} />
      </InviteAuthShell>
    )
  }

  if (stage === 'wrong') {
    return (
      <InviteAuthShell>
        <WrongAccountScreen
          company={company}
          invitedEmail={email}
          signedInEmail={Storage.getCookie(Enums.Cookie.servUserName) || email}
          onSignOut={() => { clearCookie(true); window.location.href = '/login' }}
          onCancel={() => setStage('setup')}
        />
      </InviteAuthShell>
    )
  }

  if (joinOnly) {
    return (
      <InviteAuthShell>
        <Text size="26px" lh={1.2} fw={700} c="#1C1E22" mb={6}>Join {company}</Text>
        <Text size="15px" c="#5F6468" mb={24}>
          You're signed in as <strong>{email || 'this account'}</strong>. Join {company}?
        </Text>
        <Button fullWidth color="scBlue" size="md" onClick={submit} loading={submitting} disabled={submitting}>
          Create account
        </Button>
        <Text size="13px" c="#777E82" ta="center" mt="md">After this you can sign in on web or in the ServCraft app.</Text>
        {errorMessage && <Text c="red" size="sm" mt="sm">{errorMessage}</Text>}
      </InviteAuthShell>
    )
  }

  const passwordToggle = (
    <Anchor component="button" type="button" size="xs" fw={600} c="scBlue" onClick={() => setShowPasswords((shown) => !shown)} aria-pressed={showPasswords}>
      {showPasswords ? 'Hide' : 'Show'}
    </Anchor>
  )

  return (
    <InviteAuthShell>
      <Text size="26px" lh={1.2} fw={700} c="#1C1E22" mb={6}>Join {company}</Text>
      <Text size="15px" c="#5F6468" mb={8}>
        Check your name{needsPassword ? ' and choose a password' : ''}. You'll sign in with <strong>{email}</strong>.
      </Text>
      <Text size="15px" mb={20}>
        <Anchor component="button" type="button" size="15px" fw={600} c="scBlue" onClick={() => setStage('wrong')}>
          {firstName.trim() ? `Not ${firstName.trim()}?` : 'Not you?'} This invite was sent to someone else.
        </Anchor>
      </Text>
      <Flex direction="column" gap={14}>
        <SimpleGrid cols={2} spacing={14}>
          <ScTextControl
            mt={0}
            label="First name"
            required
            size="md"
            variant="filled"
            autoComplete="given-name"
            value={firstName}
            onChange={(e: any) => setFirstName(e?.currentTarget?.value ?? e?.value ?? '')}
          />
          <ScTextControl
            mt={0}
            label="Last name"
            required
            size="md"
            variant="filled"
            autoComplete="family-name"
            value={lastName}
            onChange={(e: any) => setLastName(e?.currentTarget?.value ?? e?.value ?? '')}
          />
        </SimpleGrid>
        {needsPassword && (
          <>
            <Box>
              <PasswordInput
                label="Password"
                required
                size="md"
                variant="filled"
                autoComplete="new-password"
                visible={showPasswords}
                rightSection={passwordToggle}
                rightSectionWidth={56}
                rightSectionPointerEvents="all"
                value={password}
                error={passwordError || undefined}
                onChange={(e: any) => setPassword(e.currentTarget.value)}
              />
              <PasswordRequirements password={password} confirmPassword={confirmPassword} layout="grid" />
            </Box>
            <PasswordInput
              label="Confirm password"
              required
              size="md"
              variant="filled"
              autoComplete="new-password"
              visible={showPasswords}
              visibilityToggleButtonProps={{ style: { display: 'none' } }}
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.currentTarget.value)}
            />
          </>
        )}
        {errorMessage && <Text c="red" size="sm">{errorMessage}</Text>}
        <Button fullWidth color="scBlue" size="md" onClick={submit} loading={submitting} disabled={submitting}>
          Create account
        </Button>
        <Text size="13px" c="#777E82" ta="center">After this you can sign in on web or in the ServCraft app.</Text>
      </Flex>
    </InviteAuthShell>
  )
}

const appStoreUrl = 'https://apps.apple.com/ae/app/servcraft/id1498625938'
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=za.co.servcraft.mobile'

function DoneScreen({ company, email, firstName }: { company: string; email: string; firstName: string }) {
  return (
    <>
      <div style={{
        width: 48, height: 48, borderRadius: 24, background: '#dcfce7', color: '#16a34a',
        display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16
      }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      </div>
      <Text size="26px" lh={1.2} fw={700} c="#1C1E22" mb={6}>{firstName ? `You're in, ${firstName}` : "You're in"}</Text>
      <Text size="md" c="#4b5563" mb={24}>
        You've joined <strong>{company}</strong>. Sign in any time with <strong>{email}</strong>.
      </Text>
      <Button fullWidth color="scBlue" size="md" onClick={() => { window.location.href = '/' }}>
        Go to ServCraft
      </Button>
      <Divider my={24} />
      <Text size="sm" fw={600} c="#101828" mb={4}>Get the mobile app</Text>
      <Text size="sm" c="#4b5563" mb={14}>See your jobs and update them from site.</Text>
      <Flex gap={12} wrap="wrap" align="center">
        <a href={appStoreUrl} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
          <img src="/download-ios.png" alt="App Store" style={{ height: 48, display: 'block' }} />
        </a>
        <a href={googlePlayUrl} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
          <img src="/download-android.png" alt="Google Play" style={{ height: 52, maxWidth: 170, display: 'block' }} />
        </a>
      </Flex>
    </>
  )
}

export const Route = createFileRoute('/complete-invite')({ component: CompleteInvitePage })
export default CompleteInvitePage
